import type { MissingListStatus } from './derive-missing-list-status';
import type { MissingMailing } from './types/missing-mailing.type';

/** The per-status count field names on a {@link MissingMailing} row. */
type StatusCountField = 'errorCount' | 'goodCount' | 'missingCount' | 'pendingCount';

/** Per-status count field on a {@link MissingMailing} row, keyed by the derived {@link MissingListStatus}. */
const STATUS_COUNT_FIELD: Record<MissingListStatus, StatusCountField> = {
  error: 'errorCount',
  good: 'goodCount',
  missing: 'missingCount',
  pending: 'pendingCount',
};

/**
 * Worst-first precedence order for the package rollup dot, matching the SP's
 * `status_rank` ordering (umbrella §1): Missing → Running (pending) → Error →
 * Good. Encoded as a single ordered constant so a later confirmation of
 * umbrella open question #2 (rollup-dot ordering vs. the §7 palette) is a
 * one-line change.
 */
const STATUS_ROLLUP_ORDER: MissingListStatus[] = ['missing', 'pending', 'error', 'good'];

/**
 * Classifies a {@link MissingMailing} (package-grain) row into its single
 * rollup display status — the worst status present among the package's lists.
 *
 * @remarks
 * Unlike `deriveMissingListStatus` (which classifies a single list from three
 * booleans), this derives from the package's per-status **counts**. The
 * precedence follows the SP's `status_rank` worst-first order (Missing →
 * Running → Error → Good); a package with any Missing lists rolls up to
 * Missing regardless of how many lists are Good, and so on down the order. A
 * package with every count at zero (or absent) rolls up to Missing.
 *
 * @param row - The missing-mailing (package) row to classify.
 * @returns The derived `'error' | 'good' | 'missing' | 'pending'` rollup status.
 */
export const deriveMissingMailingStatus = (row: MissingMailing): MissingListStatus => {
  for (const status of STATUS_ROLLUP_ORDER) {
    if ((row[STATUS_COUNT_FIELD[status]] ?? 0) > 0) {
      return status;
    }
  }

  return 'missing';
};
